import React from "react";

/* ─── Base skeleton block ─── */
export function Skeleton({ className = "", style = {} }) {
  return (
    <div
      className={`skeleton ${className}`}
      style={{ borderRadius: 3, ...style }}
      aria-hidden="true"
    />
  );
}

/* ─── KPI Card skeleton ─── */
export function KpiCardSkeleton() {
  return (
    <div
      className="bg-white border border-slate-200 p-5"
      style={{ borderRadius: 3 }}
      aria-hidden="true"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Skeleton className="h-2.5 w-24 mb-3" />
          <Skeleton className="h-8 w-20 mb-2" />
          <Skeleton className="h-2 w-16" />
        </div>
        <Skeleton style={{ width: 36, height: 36, borderRadius: 3 }} />
      </div>
    </div>
  );
}

/* ─── Table row skeleton ─── */
export function TableRowSkeleton({ cols = 6 }) {
  return (
    <tr aria-hidden="true">
      {[...Array(cols)].map((_, i) => (
        <td key={i} className="py-3 px-4">
          <Skeleton className="h-3" style={{ width: `${60 + (i % 3) * 20}%` }} />
        </td>
      ))}
    </tr>
  );
}

/* ─── Full table skeleton ─── */
export function TableSkeleton({ rows = 8, cols = 6 }) {
  return (
    <div className="bg-white border border-slate-200 overflow-hidden" style={{ borderRadius: 3 }}>
      {/* Header */}
      <div className="border-b border-[#F0F2F5] px-4 py-3 flex gap-4" style={{ background: '#FAFBFC' }}>
        {[...Array(cols)].map((_, i) => (
          <Skeleton key={i} className="h-2.5" style={{ width: `${50 + (i % 4) * 15}px` }} />
        ))}
      </div>
      {/* Rows */}
      <table className="w-full">
        <tbody>
          {[...Array(rows)].map((_, i) => (
            <TableRowSkeleton key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Dashboard KPI grid skeleton ─── */
export function DashboardSkeleton() {
  return (
    <div className="space-y-7 animate-fade-in">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => <KpiCardSkeleton key={i} />)}
      </div>
      {/* Two column */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          <div className="bg-white border border-slate-200 p-5" style={{ borderRadius: 3 }}>
            <Skeleton className="h-3.5 w-32 mb-4" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
        <div>
          <div className="bg-white border border-slate-200 p-5" style={{ borderRadius: 3 }}>
            <Skeleton className="h-3.5 w-28 mb-4" />
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
                <Skeleton style={{ width: 28, height: 28, borderRadius: '50%' }} />
                <div className="flex-1">
                  <Skeleton className="h-2.5 w-32 mb-1.5" />
                  <Skeleton className="h-2 w-20" />
                </div>
                <Skeleton className="h-2.5 w-12" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Client list skeleton ─── */
export function ClientsPageSkeleton() {
  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
        <Skeleton className="h-8 w-28" />
      </div>
      <TableSkeleton rows={12} cols={7} />
    </div>
  );
}

/* ─── Card skeleton (generic) ─── */
export function CardSkeleton({ lines = 4 }) {
  return (
    <div className="bg-white border border-slate-200 p-5" style={{ borderRadius: 3 }}>
      <Skeleton className="h-3.5 w-32 mb-4" />
      {[...Array(lines)].map((_, i) => (
        <Skeleton key={i} className="h-3 mb-2.5" style={{ width: `${70 + (i % 3) * 10}%` }} />
      ))}
    </div>
  );
}
